import React, { useState } from 'react';
import { useNotify, usePermissions, useRefresh, useTranslate } from 'react-admin';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { apiClient, type ApiError } from '../../../utils/apiClient';
import { hasPermission } from '../../../utils/hasPermission';
import { canCheckWithProvider, providerRefusalKey, valueLabel } from './refundState';
import type { KioskTransactionDetail, ProviderCheck, ProviderCheckAnswer, ProviderRetryAnswer, ProviderRunOutcome } from './types';

type Severity = 'success' | 'info' | 'warning' | 'error';

export function providerCheckMessage(answer: Pick<ProviderCheckAnswer, 'check'> & { data: Pick<KioskTransactionDetail, 'canRetry' | 'refundedByHand'> }): { key: string; type: Severity } {
  const check: ProviderCheck | null = answer.check;
  switch (check) {
    case null:
      return { key: 'providerActions.checked.none', type: 'warning' };
    case 'done':
      return { key: 'providerActions.checked.done', type: 'success' };
    case 'not_done':
      if (answer.data.refundedByHand) return { key: 'providerActions.refusals.refunded_by_hand', type: 'info' };
      return { key: answer.data.canRetry ? 'providerActions.checked.not_done_retry' : 'providerActions.checked.not_done', type: 'info' };
    case 'unclear':
      return { key: 'providerActions.checked.unclear', type: 'warning' };
    case 'conflict':
      return { key: 'providerActions.checked.conflict', type: 'error' };
  }
}

const RETRY_SEVERITY: Record<ProviderRunOutcome, Severity> = {
  completed: 'success',
  failed: 'error',
  unknown: 'warning',
  retry_scheduled: 'info',
  not_claimed: 'info',
};

export const ProviderActions = ({ record }: { record: KioskTransactionDetail }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const { permissions } = usePermissions();
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const instruction = record.settleInstruction;
  const showCheck = canCheckWithProvider(record);
  const showRetry = record.canRetry && instruction !== null;
  if (!hasPermission(permissions as string[] | null | undefined, 'kiosk-transactions:write:all') || (!showCheck && !showRetry)) return null;

  const t = (key: string, options?: any) => translate(`resources.kiosk-transactions.${key}`, options);

  const reportFailure = (error: unknown) => {
    const apiError = error as ApiError;
    if (apiError.errorCode === 'KIOSK_PROVIDER_ACTION_REFUSED') {
      notify(t(providerRefusalKey(apiError.details?.reason)), { type: 'warning' });
      return;
    }
    notify(t('providerActions.failed'), { type: 'error' });
  };

  const check = async () => {
    setBusy(true);
    try {
      const answer = await apiClient.post<ProviderCheckAnswer>(`/api/backend/kiosk-transactions/${record.id}/provider-check`);
      const message = providerCheckMessage(answer);
      notify(t(message.key), { type: message.type });
    } catch (error) {
      reportFailure(error);
    } finally {
      setBusy(false);
      refresh();
    }
  };

  const retry = async () => {
    setBusy(true);
    try {
      const answer = await apiClient.post<ProviderRetryAnswer>(`/api/backend/kiosk-transactions/${record.id}/provider-retry`);
      notify(t(`providerActions.retried.${answer.outcome}`), { type: RETRY_SEVERITY[answer.outcome] });
    } catch (error) {
      reportFailure(error);
    } finally {
      setConfirmOpen(false);
      setBusy(false);
      refresh();
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {showCheck && (
        <Button variant="outlined" onClick={check} disabled={busy}>
          {t('providerActions.check')}
        </Button>
      )}
      {showRetry && (
        <Button variant="contained" color="warning" onClick={() => setConfirmOpen(true)} disabled={busy}>
          {t('providerActions.retry')}
        </Button>
      )}
      {showRetry && (
        <Dialog open={confirmOpen} onClose={() => { if (!busy) setConfirmOpen(false); }} fullWidth maxWidth="sm">
          <DialogTitle>{t('providerActions.retryTitle')}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t('providerActions.retryBody', {
                checkedAt: instruction.lastCheckedAt === null ? '-' : new Date(instruction.lastCheckedAt).toLocaleString(),
                call: valueLabel(translate, 'settleTypes', instruction.type),
                amount: instruction.amount.toFixed(2),
                reference: record.receiptReference,
              })}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)} disabled={busy}>{translate('ra.action.cancel')}</Button>
            <Button variant="contained" color="warning" onClick={retry} disabled={busy}>
              {t('providerActions.retryConfirm')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};